import React, { useRef, useState } from "react";
import emailjs from "emailjs-com";
import { motion } from "framer-motion";
import {
  Mail,
  Send,
  Github,
  Linkedin,
  CheckCircle2,
  Loader2,
} from "lucide-react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Contact = () => {
  const form = useRef(null);
  const [isSending, setIsSending] = useState(false);

  const sendEmail = async (event) => {
    event.preventDefault();

    if (isSending) {
      return;
    }

    setIsSending(true);

    try {
      await emailjs.sendForm(
        "service_2zqakt5",
        "template_p9j0shh",
        form.current,
        "xq5-71L7rinIQrjsZ",
      );

      toast.success("Message sent successfully!");

      form.current.reset();
    } catch (error) {
      console.error("EmailJS Error:", error);

      toast.error("Unable to send your message. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-white/10 bg-white/[0.035] px-4 py-3.5 text-sm text-white outline-none transition placeholder:text-gray-600 focus:border-orange-500/40 focus:bg-orange-500/[0.025] focus:ring-4 focus:ring-orange-500/5";

  return (
    <section
      id="contact"
      className="relative overflow-hidden bg-[#07070a] px-6 py-28 text-white sm:px-10 lg:px-20"
    >
      <ToastContainer position="bottom-right" autoClose={3500} theme="dark" />

      <div className="absolute left-1/2 top-0 h-96 w-96 -translate-x-1/2 rounded-full bg-orange-500/[0.045] blur-[120px]" />

      <div className="relative z-10 mx-auto max-w-6xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-orange-400">
            Contact
          </p>

          <h2 className="text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
            Let's build something{" "}
            <span className="text-orange-500">great.</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-7 text-gray-500">
            Have a project, opportunity or idea? Send me a message and I'll get
            back to you.
          </p>
        </motion.div>

        <div className="mt-14 grid gap-8 lg:grid-cols-[0.75fr_1.25fr]">
          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-7"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-orange-500/20 bg-orange-500/10 text-orange-400">
              <Mail size={21} />
            </div>

            <h3 className="mt-7 text-2xl font-bold">Get in touch</h3>

            <p className="mt-3 text-sm leading-7 text-gray-500">
              I'm always interested in discussing software engineering, backend
              development, new opportunities and interesting projects.
            </p>

            <a
              href="mailto:arunpunglegto15@gmail.com"
              className="mt-7 block rounded-2xl border border-white/10 bg-black/20 p-4 transition hover:border-orange-500/20"
            >
              <p className="text-[10px] uppercase tracking-widest text-gray-600">
                Email
              </p>

              <p className="mt-1 break-all text-sm font-medium text-orange-400">
                arunpunglegto15@gmail.com
              </p>
            </a>

            <div className="mt-6 flex gap-3">
              <a
                href="https://github.com/ArunPungle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-500 transition hover:border-orange-500/20 hover:text-orange-400"
              >
                <Github size={18} />
              </a>

              <a
                href="https://www.linkedin.com/in/arun-pungle"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/[0.03] text-gray-500 transition hover:border-orange-500/20 hover:text-orange-400"
              >
                <Linkedin size={18} />
              </a>
            </div>

            <div className="mt-8 flex items-center gap-2 text-xs text-emerald-400">
              <CheckCircle2 size={15} />
              Open to opportunities
            </div>
          </motion.div>

          {/* Form */}
          <motion.form
            ref={form}
            onSubmit={sendEmail}
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-white/10 bg-white/[0.025] p-7"
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="from_name"
                  className="mb-2 block text-xs font-medium text-gray-500"
                >
                  Name
                </label>

                <input
                  id="from_name"
                  name="from_name"
                  type="text"
                  required
                  placeholder="Your name"
                  className={inputClass}
                />
              </div>

              <div>
                <label
                  htmlFor="from_email"
                  className="mb-2 block text-xs font-medium text-gray-500"
                >
                  Email
                </label>

                <input
                  id="from_email"
                  name="from_email"
                  type="email"
                  required
                  placeholder="you@example.com"
                  className={inputClass}
                />
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="subject"
                  className="mb-2 block text-xs font-medium text-gray-500"
                >
                  Subject
                </label>

                <input
                  id="subject"
                  name="subject"
                  type="text"
                  required
                  placeholder="How can I help?"
                  className={inputClass}
                />
              </div>

              <div className="sm:col-span-2">
                <label
                  htmlFor="message"
                  className="mb-2 block text-xs font-medium text-gray-500"
                >
                  Message
                </label>

                <textarea
                  id="message"
                  name="message"
                  rows="7"
                  required
                  placeholder="Tell me a little about your project..."
                  className={`${inputClass} resize-none`}
                />
              </div>

              <div className="sm:col-span-2">
                <button
                  type="submit"
                  disabled={isSending}
                  className="group flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3.5 font-semibold text-white shadow-lg shadow-orange-500/10 transition hover:-translate-y-0.5 hover:shadow-orange-500/20 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSending ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send
                        size={17}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
